/**
 * Shared speaker identity, display formatting, and color assignment for diarization UIs.
 */

/**
 * Pastel palette for a stable color per unique speaker key.
 * Mid-luminance (relative L ≈ 0.33–0.55) so bars stay visible on light tracks —
 * no global lighten (that washed soft pastels toward paper white).
 */
export const SPEAKER_COLORS = [
  '#FF9AA2', // pastel pink
  '#7EAAC7', // pastel blue
  '#32DAC3', // pastel green (darkened)
  '#D8B384', // pastel beige
  '#A39EC9', // pastel purple
  '#FF8C42', // pastel orange
  '#70D6B3', // pastel mint
  '#FF6F61', // pastel coral
  '#9F94F0', // pastel lavender
  '#FFA07A', // pastel peach
  '#FFA9A3', // pastel blush (darkened)
  '#D4A5A5', // pastel rose
  '#90CCE4', // pastel sky blue (darkened)
  '#61D5AD', // pastel seafoam (darkened)
  '#B1BAE2', // pastel periwinkle (darkened)
  '#FFAF7A', // pastel apricot (darkened)
  '#F2ADA5', // pastel salmon (darkened)
  '#9CD253', // pastel light lime (darkened)
  '#CEBCDB', // pastel lilac (darkened)
  '#D9B5B5', // pastel blush pink (darkened)
];

const FALLBACK_SPEAKER_COLOR = '#00BCD4'; // aqua-like fallback

/** Returns true when the string is a number (used to strip trailing ids from voice labels). */
export function isNumeric(value: string): boolean {
  return /^[+-]?\d+(\.\d+)?$/.test(value);
}

/**
 * Formats a raw voice_label / SPEAKER_N label into a human-readable string.
 * SPEAKER labels keep underscores as spaces; other labels drop numeric segments.
 */
export function formatVoiceLabel(voiceLabel?: string, splitter = '-', joiner = ' '): string {
  const isSpeaker = voiceLabel?.includes('SPEAKER');
  if (isSpeaker) {
    return voiceLabel ? voiceLabel.split('_').join(joiner) : '';
  }
  return voiceLabel
    ? voiceLabel
        .split(splitter)
        .filter((value: string) => !isNumeric(value))
        .join(joiner)
    : '';
}

/**
 * Title-cases a formatted voice label for display in tables / markers.
 */
export function formatSpeakerDisplayLabel(label: string): string {
  return label
    .split(' ')
    .map((item: string) => item.charAt(0).toUpperCase() + item.slice(1).toLowerCase())
    .filter((value: string) => !isNumeric(value))
    .join(' ');
}

/**
 * Compact initials from a voice label for focus-strip markers (first letter of each segment).
 */
export function getVoiceLabelInitials(voiceLabel?: string): string {
  if (!voiceLabel) {
    return '';
  }
  return voiceLabel
    .split('-')
    .map((item: string) => item[0]?.toUpperCase() ?? '')
    .filter((value: string) => !isNumeric(value))
    .join('');
}

type SpeakerIdentityFields = {
  person_id?: string | null;
  voice_label?: string;
  label?: string;
};

/**
 * Stable identity for color assignment and dedupe: prefer person_id, else formatted voice_label.
 */
export function getSpeakerKey(d: SpeakerIdentityFields): string {
  if (d.person_id) {
    return d.person_id;
  }
  return formatVoiceLabel(d.voice_label) || formatVoiceLabel(d.label) || '';
}

/**
 * Whether a diarization entry should appear in logger speaker lists / markers
 * (excludes SILENCE, NO IDENTIFICADO, and anonymous SPEAKER_* without a person).
 */
export function isDisplayableDiarization(d: SpeakerIdentityFields | null | undefined): boolean {
  if (!d) {
    return false;
  }

  const label = d.voice_label || d.label;

  if (label === 'SILENCE' || label?.includes('NO IDENTIFICADO')) {
    return false;
  }

  // Engine placeholder labels without a matched Person are treated as unidentified.
  // Accept raw (SPEAKER_01) and already-formatted (SPEAKER 01) labels.
  const rawLabel = d.voice_label || d.label;
  if (!d.person_id && /^SPEAKER(?:_|[\s-])/.test(rawLabel ?? '')) {
    return false;
  }

  return true;
}

export type SpeakerColorEntry = {
  /** Identity key (person_id or formatted voice_label). */
  key: string;
  /** Fallback display label from voice_label when person name is unavailable. */
  label: string;
  color: string;
  personId?: string;
};

/**
 * Builds an ordered list of unique speakers with assigned colors from diarization entries.
 */
export function buildSpeakerColorMap(entries: SpeakerIdentityFields[]): SpeakerColorEntry[] {
  const speakers: SpeakerColorEntry[] = [];
  const seen = new Set<string>();

  for (const d of entries) {
    if (!isDisplayableDiarization(d)) {
      continue;
    }
    const key = getSpeakerKey(d);
    if (!key || seen.has(key)) {
      continue;
    }
    seen.add(key);

    speakers.push({
      key,
      label: formatVoiceLabel(d.voice_label) || formatVoiceLabel(d.label) || key,
      color: SPEAKER_COLORS[speakers.length % SPEAKER_COLORS.length] ?? FALLBACK_SPEAKER_COLOR,
      personId: d.person_id ?? undefined,
    });
  }

  return speakers;
}

/**
 * Looks up the color assigned to a diarization entry in a speakers list.
 */
export function getSpeakerColor(
  speakers: SpeakerColorEntry[],
  d: SpeakerIdentityFields,
): string | undefined {
  const key = getSpeakerKey(d);
  return speakers.find((s) => s.key === key)?.color;
}
